import React from "react";

import { List as MUIList, ListItem, ListItemIcon, ListItemText, Collapse } from "@mui/material";
import { ExpandMore, ExpandLess, ArrowBack, ArrowForward } from "@mui/icons-material";

import useStyles from "./styles";

type SubItem = {
  open: boolean;
  items: Item[];
};

type BackButton = {
  isShowing: boolean;
  action: () => void;
};

type Item = {
  label: string;
  currentSubPage?: string;
  subitems?: SubItem;
  iconRight?: boolean;
  action: () => void;
};

type ExtraComponent = {
  label: string;
  component: JSX.Element[];
};

export type List = {
  listOfItems: Item[];
  extraComponents?: ExtraComponent[];
};

interface Props {
  currentPage: string;
  list: List;
  backButton?: BackButton;
}

const CollapseLoop = ({
  dataCy,
  list,
  currentPage,
}: {
  dataCy?: string;
  list: SubItem;
  currentPage: string;
}) => {
  const classes = useStyles();

  return (
    <Collapse in={list.open} timeout="auto" unmountOnExit>
      <MUIList component="div">
        {list.items.map((subitem) => {
          if (Object.keys(subitem).length > 0) {
            return (
              <div className="pl-2" key={subitem.label}>
                <ListItem
                  button
                  className={classes.sidebarSubItem}
                  onClick={subitem.action}
                  data-cy={subitem.label.toLowerCase().replace(/\s+/g, "")}
                >
                  <ListItemText
                    className={`${classes.sidebarItemsText} ${classes.sidebarSubItemText} ${
                      currentPage?.toLowerCase()?.replaceAll(" ", "") === subitem?.label?.toLowerCase()?.replaceAll(" ", "") ? "active" : ""
                    }`}
                    primary={subitem.label}
                  />
                  {subitem.iconRight && <ArrowForward className={classes.sidebarForwardIcon} />}
                  {subitem.subitems && (subitem.subitems.open ? <ExpandLess /> : <ExpandMore />)}
                </ListItem>
                {subitem.subitems && (
                  <CollapseLoop
                    dataCy={`${subitem.label.toLowerCase().replace(/\s+/g, "")}Nav`}
                    currentPage={subitem.currentSubPage || ""}
                    list={subitem.subitems}
                  />
                )}
              </div>
            );
          }
        })}
      </MUIList>
    </Collapse>
  );
};

const GenericSidebar = ({ currentPage, list, backButton }: Props) => {
  const classes = useStyles();

  return (
    <div className={classes.wrapper}>
      <section data-cy="sidebar">
        <MUIList component="nav">
          {backButton && backButton.isShowing === true && (
            <ListItem button onClick={backButton.action} data-cy="backToSettingsButton">
              <ListItemIcon>
                <ArrowBack className={classes.sidebarForwardIcon} />
              </ListItemIcon>
              <ListItemText className={classes.sidebarItemsText} primary="Back to settings" />
            </ListItem>
          )}
          {list.listOfItems.map(
            (item) =>
              Object.keys(item).length > 0 && (
                <div key={item.label}>
                  <ListItem
                    button
                    className={classes.sidebarItems}
                    onClick={item.action}
                    data-cy={item.label.toLowerCase().replace(/\s+/g, "")}
                  >
                    <ListItemText
                      className={`${classes.sidebarItemsText} ${
                        currentPage.toLowerCase() === item.label.toLowerCase()
                          ? "active"
                          : item.subitems && item?.subitems?.open
                          ? "active"
                          : ""
                      }`}
                      primary={item.label}
                    />
                    {item.iconRight && <ArrowForward className={classes.sidebarForwardIcon} />}
                    {item.subitems && (item.subitems.open ? <ExpandLess /> : <ExpandMore />)}
                  </ListItem>
                  {item.subitems && (
                    <CollapseLoop
                      dataCy={`${item.label.toLowerCase().replace(/\s+/g, "")}Nav`}
                      currentPage={item.currentSubPage || ""}
                      list={item.subitems}
                    />
                  )}
                </div>
              )
          )}
          {list.extraComponents &&
            list.extraComponents?.map((component) => (
              <ListItem className={`${classes.sidebarItems} d-flex align-items-start flex-column`}>
                <ListItemText
                  className={`active ${classes.sidebarItemsText}`}
                  primary={component.label}
                />
                {component.component &&
                  component.component.map((item: JSX.Element, index: number) => (
                    <div className="w-100" key={index}>
                      {item}
                    </div>
                  ))}
              </ListItem>
            ))}
        </MUIList>
      </section>
    </div>
  );
};

export default GenericSidebar;
